const AES_KEY_LENGTH_BYTES = 32;
export const AES_GCM_IV_LENGTH_BYTES = 12;
const AES_GCM_TAG_LENGTH_BITS = 128;

const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => Uint8Array.from(bytes).buffer;

const assertLength = (bytes: Uint8Array, expected: number, label: string): void => {
  if (bytes.byteLength !== expected) {
    throw new RangeError(`${label} must contain ${expected} bytes`);
  }
};

export class Aes256Gcm {
  constructor(private readonly subtle: SubtleCrypto = globalThis.crypto.subtle) {}

  async importKey(rawKey: Uint8Array): Promise<CryptoKey> {
    assertLength(rawKey, AES_KEY_LENGTH_BYTES, 'AES-256 key');
    return this.subtle.importKey('raw', toArrayBuffer(rawKey), { name: 'AES-GCM' }, false, [
      'encrypt',
      'decrypt',
    ]);
  }

  async encrypt(
    plaintext: Uint8Array<ArrayBuffer>,
    key: CryptoKey,
    iv: Uint8Array,
    associatedData: Uint8Array,
  ): Promise<Uint8Array> {
    assertLength(iv, AES_GCM_IV_LENGTH_BYTES, 'AES-GCM IV');
    const encrypted = await this.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: toArrayBuffer(iv),
        additionalData: toArrayBuffer(associatedData),
        tagLength: AES_GCM_TAG_LENGTH_BITS,
      },
      key,
      plaintext,
    );
    return new Uint8Array(encrypted);
  }

  async decrypt(
    ciphertext: Uint8Array,
    key: CryptoKey,
    iv: Uint8Array,
    associatedData: Uint8Array,
  ): Promise<Uint8Array> {
    assertLength(iv, AES_GCM_IV_LENGTH_BYTES, 'AES-GCM IV');
    const decrypted = await this.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: toArrayBuffer(iv),
        additionalData: toArrayBuffer(associatedData),
        tagLength: AES_GCM_TAG_LENGTH_BITS,
      },
      key,
      toArrayBuffer(ciphertext),
    );
    return new Uint8Array(decrypted);
  }
}
